import React, { createContext, useCallback, useEffect, useReducer, useRef } from "react";
import { blankBiz, blankPreparer, SEEDS } from "../data/rates.js";

// businessId/preparerId/returnId are mirrored into sessionStorage (survives
// a hard refresh within the same tab, same as api.js's stored user) so a
// refresh on Business/Tax Preparer/Trucks step doesn't lose them - without
// this, the in-memory-only React state resets to null on reload, the
// steps' "if (!businessId) return;" fetch guards short-circuit, and the
// saved data never reloads even though the user is still logged in.
const PERSISTED_ID_KEYS = {
  businessId: "trucktax_businessId",
  preparerId: "trucktax_preparerId",
  returnId: "trucktax_returnId",
};

function loadPersistedId(key) {
  const raw = sessionStorage.getItem(key);
  return raw ? Number(raw) : null;
}

function persistId(key, value) {
  if (value) sessionStorage.setItem(key, String(value));
  else sessionStorage.removeItem(key);
}

// returnType/amendReason need the same survives-a-refresh treatment as the
// three ids above, but for a different reason: unlike Original/Final (saved
// to the backend the moment FormTypeStep's Continue is clicked), Amended and
// VIN Correction only persist formTypeCd/amendReasonCd server-side on THEIR
// OWN step's Continue (AmendedReturnStep/WeightIncreaseStep/
// VinCorrectionStep - see FormTypeStep's handleContinue comment). So a
// refresh before that point can't recover the filer's choice from the
// backend at all - it was never sent. Without this, Wizard.jsx's
// buildSteps(state) rebuilds the plain Original-shaped step sequence (no
// amendedReturn/vinCorrection/amendDetail step inserted) on every such
// refresh, and whatever position the URL still points at silently resolves
// to whatever step occupies that slot in the WRONG sequence (Trucks, Review,
// etc.) instead of the page the filer was actually on.
const PERSISTED_STRING_KEYS = {
  returnType: "trucktax_returnType",
  amendReason: "trucktax_amendReason",
};

function loadPersistedString(key) {
  return sessionStorage.getItem(key) || "";
}

function persistString(key, value) {
  if (value) sessionStorage.setItem(key, String(value));
  else sessionStorage.removeItem(key);
}

const initialState = {
  status: "new",
  step: 1,
  maxStep: 1,
  // Set from the login/signup response's user.taxPreparer. null = unknown
  // (no login this session yet) - Wizard.jsx only skips the Tax Preparer
  // step when this is the explicit boolean false, never on null.
  isTaxPreparer: null,
  // Set once BusinessDetails is saved to the backend (BusinessStep's
  // "Continue to trucks"). Used to reload a saved business on return.
  businessId: loadPersistedId(PERSISTED_ID_KEYS.businessId),
  // Set as soon as the Tax Preparer step saves (its own Continue click) - an
  // independent record, not tied to any business. The Business step's
  // optional "Tax Preparer" dropdown references preparer ids, not this one
  // directly, but this is what lets the Tax Preparer step itself PUT instead
  // of POST on a later visit.
  preparerId: loadPersistedId(PERSISTED_ID_KEYS.preparerId),
  biz: blankBiz(),
  preparer: blankPreparer(),
  // Per-return preparer override, set via the Form step's "Tax Preparer" dropdown
  // (FormTypeStep.jsx) - distinct from biz.taxPreparerId, which is the BUSINESS's own
  // saved default (editable from the standalone Business form, reused by BusinessStep).
  // null means "no override for this filing" (falls back to biz.taxPreparerId for display,
  // and to the business's default server-side - see TaxReturn.taxPreparer's javadoc). Not
  // sessionStorage-persisted - re-hydrated from the backend on every mount via SET_RETURN,
  // same as returnTotals/serviceFee.
  returnTaxPreparerId: null,
  // Form Type step - filing type + IRS2290.xsd AmendedReturnInd/VINCorrectionInd
  // details. Lifted to global state (rather than component-local) because the
  // amended-return flow now spans several wizard steps (Amended Return,
  // Weight Increase, Mileage Exceeded, VIN Correction) that must all read and
  // write the same selections as the user moves between them.
  returnType: loadPersistedString(PERSISTED_STRING_KEYS.returnType), // original | amended | vincorrection | final
  finalReturn: false,
  // The IRS filing period (e.g. "2026" = July 2026-June 2027). Selected
  // on each filing form (Trucks/Original, VIN Correction, Amended -
  // mileage reason) alongside that form's own Month first used field.
  year: "2026",
  addressChange: false,
  amendReason: loadPersistedString(PERSISTED_STRING_KEYS.amendReason), // weight | mileage - AmendedReturnInd is only ever checked for these two IRS-sanctioned reasons
  amendMonth: "", // AmendedMonthNum for a weight-increase amendment
  amendVehicles: [], // TGWIncreaseWorksheet rows: {id, vin, oldCat, newCat, logging}
  amendMonthMileage: "", // AmendedMonthNum for a mileage-limit-exceeded amendment
  vinCorrectionExpl: "", // VINCorrectionExplanationStmt/ExplanationTxt
  correctedVins: [], // corrected VIN(s) listed on Schedule 1 for a VIN correction: {id, vin}
  month: "2026-07",
  trucks: [],
  // Amended Return -> "Suspended vehicle went over its mileage limit" has
  // its own vehicle list, isolated from `trucks` (Original Return) - same
  // pattern as amendVehicles/correctedVins below. Populated locally
  // (ADD_MILEAGE_VEHICLE etc., unauthenticated fallback) or from the
  // backend's echoed vehicle list via SET_RETURN once amendReason is
  // "mileage" (see that case below - the backend returns one vehicle list
  // per return; which bucket it lands in is a frontend routing decision).
  mileageVehicles: [],
  // Set once the current business's draft return is fetched/created on the
  // backend (TrucksStep). Null means "no backend return loaded yet" - the
  // signal useTotals() uses to fall back to local computation for the
  // unauthenticated dashboard-demo path (see useTotals.js).
  returnId: loadPersistedId(PERSISTED_ID_KEYS.returnId),
  returnTotals: null,
  // Set by RESET_RETURN (FormTypeStep's Business dropdown) to tell
  // Wizard.jsx's fetch-or-create effect it must call createFreshReturnForBusiness
  // (always a brand-new draft) instead of getReturnForBusiness (which reuses
  // whatever half/new/full draft already exists for that business, however old
  // or abandoned). Not sessionStorage-persisted - only meant to steer the very
  // next fetch after a business switch, cleared the moment SET_RETURN lands.
  forceFreshDraft: false,
  // Rejected-return-resubmission vehicle info (TrucksStep's "Vehicles: X/Y" note)
  // - set from the backend's TaxReturnResponse via SET_RETURN below; display-only.
  // Adding vehicles past originalVehicleCount is no longer blocked (2026-08-17) -
  // extraVehiclesPendingFee is the real current gate: > 0 means an additional
  // service fee is owed before this return can be resubmitted, enforced at
  // submit time by Return2290XmlServiceImpl.requireReturnIsPaidFor. Not
  // sessionStorage-persisted, same reasoning as returnTotals/serviceFee -
  // re-fetched from the backend on every mount via the already-persisted returnId.
  rejectedResubmission: false,
  originalVehicleCount: null,
  remainingVehicleCapacity: null,
  extraVehiclesPendingFee: null,
  perCategory: [],
  // Dropdown data fetched once by TrucksStep (GET /lookups/weight-categories,
  // GET /lookups/months) - shared with ReviewStep's month label. Empty
  // arrays fall back to the static data/rates.js lists (dashboard-demo path).
  weightCategoriesLookup: [],
  monthsLookup: [],
  // GET /lookups/tax-years - fetched by each filing form (Trucks, VIN
  // Correction, Amended) alongside its own Month lookup.
  taxYearsLookup: [],
  pay: { method: "", routing: "", account: "", accountType: "" },
  // One-time e-filing service fee (Payment Details step, Stripe) - separate
  // from `pay` (how the tax owed is paid). Not sessionStorage-persisted:
  // re-fetched from the backend on every mount, same as the other
  // backend-sourced steps, since it's keyed off returnId which IS persisted.
  serviceFee: { status: "", paymentId: null },
  filed: false,
  // Backend submit response ({ returnId, status, irsStatus, submissionId, submittedAt })
  // once the return is filed - shown on the confirmation screen.
  filedResult: null,
  toast: "",
};

function reducer(state, action) {
  switch (action.type) {
    case "SEED": {
      const seed = SEEDS[action.which];
      return {
        ...initialState,
        isTaxPreparer: state.isTaxPreparer,
        // Always start fresh here, even if a prior session left a
        // persisted id (or, now, a persisted returnType/amendReason - see
        // PERSISTED_STRING_KEYS) behind - "New Filing"/"New Business" must
        // never silently resume someone else's saved record or in-progress
        // filing-type choice.
        businessId: null,
        preparerId: null,
        returnId: null,
        returnType: "",
        amendReason: "",
        returnTotals: null,
        perCategory: [],
        status: action.which,
        biz: { ...seed.biz },
        preparer: { ...seed.preparer },
        trucks: seed.trucks.map((t) => ({ ...t })),
        mileageVehicles: [],
        maxStep: seed.maxStep,
        step: seed.step,
      };
    }
    case "GO_STEP":
      return { ...state, step: action.step, maxStep: Math.max(state.maxStep, action.step) };
    case "SET_IS_TAX_PREPARER":
      return { ...state, isTaxPreparer: action.isTaxPreparer };
    case "SET_BIZ":
      return { ...state, biz: { ...state.biz, ...action.patch } };
    case "SET_BUSINESS_ID":
      return { ...state, businessId: action.businessId };
    case "SET_PREPARER":
      return { ...state, preparer: { ...state.preparer, ...action.patch } };
    case "SET_PREPARER_ID":
      return { ...state, preparerId: action.preparerId };
    // FormTypeStep's "Tax Preparer" dropdown - a per-return override, deliberately NOT
    // routed through SET_BIZ/biz.taxPreparerId (that field is the business's own saved
    // default, edited from the standalone Business form). See returnTaxPreparerId's comment
    // in initialState above.
    case "SET_RETURN_TAX_PREPARER":
      return { ...state, returnTaxPreparerId: action.taxPreparerId };
    // Changing the filing type (or amendment reason) reshapes the step
    // sequence below this point (stepConfig.js) - capping maxStep to the
    // current step prevents stale "done" markers from a differently-shaped
    // sequence bleeding into the new one (e.g. switching from Original,
    // where step 8 was Tax Summary, to Amended, where step 8 is now Trucks).
    case "SET_RETURN_TYPE":
      return {
        ...state,
        returnType: action.returnType,
        finalReturn: action.returnType === "final" ? true : false,
        maxStep: Math.min(state.maxStep, state.step),
      };
    case "SET_FINAL_RETURN":
      return { ...state, finalReturn: action.value };
    case "SET_TAX_YEAR":
      return { ...state, year: action.year };
    case "SET_ADDRESS_CHANGE":
      return { ...state, addressChange: action.value };
    case "SET_AMEND_REASON":
      return { ...state, amendReason: action.reason, maxStep: Math.min(state.maxStep, state.step) };
    case "SET_AMEND_MONTH":
      return { ...state, amendMonth: action.month };
    case "ADD_AMEND_VEHICLE":
      return { ...state, amendVehicles: [...state.amendVehicles, action.vehicle] };
    case "UPDATE_AMEND_VEHICLE":
      return {
        ...state,
        amendVehicles: state.amendVehicles.map((v) => (v.id === action.id ? { ...v, ...action.patch } : v)),
      };
    case "REMOVE_AMEND_VEHICLE":
      return { ...state, amendVehicles: state.amendVehicles.filter((v) => v.id !== action.id) };
    // Bulk sibling of REMOVE_AMEND_VEHICLE (WeightIncreaseStep's checkbox
    // multi-select) - amendVehicles rows always carry a client-generated id
    // (nextRowId++) regardless of usingBackend, since this list is never
    // itself persisted per-row (only in bulk via saveFormType on Continue),
    // so there's no backend call to make here either way.
    case "BULK_REMOVE_AMEND_VEHICLES":
      return { ...state, amendVehicles: state.amendVehicles.filter((v) => !action.ids.has(v.id)) };
    case "SET_AMEND_MONTH_MILEAGE":
      return { ...state, amendMonthMileage: action.month };
    case "SET_VIN_CORRECTION_EXPL":
      return { ...state, vinCorrectionExpl: action.text };
    case "ADD_CORRECTED_VIN":
      return { ...state, correctedVins: [...state.correctedVins, action.vehicle] };
    case "UPDATE_CORRECTED_VIN":
      return {
        ...state,
        correctedVins: state.correctedVins.map((v) => (v.id === action.id ? { ...v, ...action.patch } : v)),
      };
    case "REMOVE_CORRECTED_VIN":
      return { ...state, correctedVins: state.correctedVins.filter((v) => v.id !== action.id) };
    // Bulk sibling of REMOVE_CORRECTED_VIN - local/no-backend fallback only
    // (usingBackend calls deleteVinCorrectionsBulk and dispatches SET_RETURN
    // with the server's fresh row list instead, same split as every other
    // bulk-delete here).
    case "BULK_REMOVE_CORRECTED_VINS":
      return { ...state, correctedVins: state.correctedVins.filter((v) => !action.ids.has(v.id)) };
    case "SET_MONTH":
      return { ...state, month: action.month };
    case "ADD_TRUCK":
      return { ...state, trucks: [...state.trucks, action.truck] };
    case "REMOVE_TRUCK":
      return { ...state, trucks: state.trucks.filter((_, i) => i !== action.index) };
    // Bulk sibling of REMOVE_TRUCK - local/no-backend fallback only
    // (usingBackend calls deleteVehiclesBulk and dispatches SET_RETURN with
    // the server's fresh vehicle list instead). Keyed by index, not id -
    // local-mode trucks have no backend id (see TrucksTable's checkbox
    // selection key, t.id ?? i) - so action.indices is a Set of positions,
    // not row ids.
    case "BULK_REMOVE_TRUCKS":
      return { ...state, trucks: state.trucks.filter((_, i) => !action.indices.has(i)) };
    // Local/no-backend fallback edit-in-place (dashboard-demo path, where
    // trucks have no backend id to key off - see AmendedReturnStep).
    case "UPDATE_TRUCK":
      return { ...state, trucks: state.trucks.map((t, i) => (i === action.index ? { ...t, ...action.patch } : t)) };
    // Mileage-exceeded vehicle list (AmendedReturnStep's "mileage" reason) -
    // isolated from ADD_TRUCK/state.trucks above; same shape/semantics,
    // just a separate bucket so Original Return and Suspended Vehicle never
    // share data (see SET_RETURN's routing below for the backend path).
    case "ADD_MILEAGE_VEHICLE":
      return { ...state, mileageVehicles: [...state.mileageVehicles, action.truck] };
    case "REMOVE_MILEAGE_VEHICLE":
      return { ...state, mileageVehicles: state.mileageVehicles.filter((_, i) => i !== action.index) };
    // Bulk sibling of REMOVE_MILEAGE_VEHICLE - see BULK_REMOVE_TRUCKS above
    // for why this is index-keyed and local-fallback only.
    case "BULK_REMOVE_MILEAGE_VEHICLES":
      return { ...state, mileageVehicles: state.mileageVehicles.filter((_, i) => !action.indices.has(i)) };
    case "UPDATE_MILEAGE_VEHICLE":
      return {
        ...state,
        mileageVehicles: state.mileageVehicles.map((t, i) => (i === action.index ? { ...t, ...action.patch } : t)),
      };
    // Replaces trucks/month/totals wholesale from a backend TaxReturn
    // response (getReturnForBusiness / updateReturnMonth / addVehicle /
    // updateVehicle / deleteVehicle in trucksService.js, or
    // updateReturnYearMonth / saveFormType in returnService.js) - the
    // backend is the source of truth once a return has been loaded.
    //
    // The form-type/amendment/VIN-correction fields below use `??`/`||`
    // fallbacks to the current local value rather than overwriting outright:
    // this action also fires after every truck add/remove (TrucksStep,
    // AmendedReturnStep's mileage-exceeded section), at which point the user may have already picked
    // a filing type locally that the backend hasn't been told about yet
    // (form-type is only saved once its own step's data is complete) - a
    // blank server value must never clobber that in-progress local choice.
    case "SET_RETURN": {
      // The backend returns one vehicle list per return - correct, since a
      // return is either an Original return or a Mileage-exceeded Amended
      // return, never both. Which frontend bucket it lands in is purely a
      // routing decision here: state.trucks for everything else, and the
      // isolated state.mileageVehicles once this return's context is
      // amended+mileage (falls back to the current state's returnType/
      // amendReason when this particular response doesn't echo them, same
      // reasoning as the fallbacks below).
      const isMileageCtx =
        (action.returnType || state.returnType) === "amended" &&
        (action.amendReason || state.amendReason) === "mileage";
      // Resolve finalReturn first so returnType can be derived from it below -
      // once the filer has locally checked "This is my final return" (or the
      // backend itself confirms formTypeCd "final"), that must win over a
      // stale/default "original" echoed back by an auto-created draft (see
      // getReturnForBusiness) that just hasn't been told about the final
      // choice yet. Without this, checking "final" on a brand-new filing
      // (before any return/totals exist) triggers Wizard.jsx's draft
      // fetch-or-create, whose "original" default silently clobbers
      // returnType back while finalReturn stays true - the UI keeps showing
      // "final return" (checkbox, optional Trucks step) but formTypeCd
      // actually gets saved as "original", so XML validate later rejects it
      // for having zero vehicles.
      const finalReturn = action.returnType === "final" ? true : (action.finalReturn ?? state.finalReturn);
      return {
        ...state,
        returnId: action.returnId,
        forceFreshDraft: false,
        year: action.year || state.year,
        month: action.month,
        trucks: isMileageCtx ? state.trucks : action.trucks,
        mileageVehicles: isMileageCtx ? action.trucks : state.mileageVehicles,
        returnTotals: action.totals,
        perCategory: action.perCategory,
        returnType: finalReturn ? "final" : (action.returnType || state.returnType),
        finalReturn,
        addressChange: action.addressChange ?? state.addressChange,
        amendReason: action.amendReason || state.amendReason,
        amendMonth: action.amendMonth || state.amendMonth,
        amendMonthMileage: action.amendMonthMileage || state.amendMonthMileage,
        vinCorrectionExpl: action.vinCorrectionExpl || state.vinCorrectionExpl,
        // `?.length ? ... : state.X` used to treat a genuinely-empty backend
        // list (e.g. the last row just got deleted) the same as "this field
        // wasn't echoed back", silently keeping the stale pre-delete rows in
        // the UI even though the server had already removed them - the next
        // delete/edit of one of those ghost rows then 404s. Every caller
        // spreads `...r` from fromReturnResponse (trucksService.js), which
        // always sets both keys to an array (possibly []), never undefined -
        // so checking `!== undefined` instead of `?.length` still falls back
        // for a hand-built partial action that truly omits the key, while no
        // longer discarding a real "now empty" list.
        amendVehicles: action.amendVehicles !== undefined ? action.amendVehicles : state.amendVehicles,
        correctedVins: action.correctedVins !== undefined ? action.correctedVins : state.correctedVins,
        // Always the fresh backend value (never client-derived/editable, unlike the
        // filing-type fields above) - defaults match initialState for any caller that
        // doesn't echo them.
        rejectedResubmission: !!action.rejectedResubmission,
        originalVehicleCount: action.originalVehicleCount ?? null,
        remainingVehicleCapacity: action.remainingVehicleCapacity ?? null,
        extraVehiclesPendingFee: action.extraVehiclesPendingFee ?? null,
        // Same "don't let a missing echo clobber an in-progress local choice" fallback as
        // the filing-type fields above - but unlike those, an explicit `null` here (the
        // backend's real answer: "this return has no preparer override") must still win,
        // so only a genuinely absent key (undefined) falls back to the current value.
        returnTaxPreparerId: action.taxPreparerId !== undefined ? action.taxPreparerId : state.returnTaxPreparerId,
      };
    }
    // FormTypeStep's Business dropdown - switching to a DIFFERENT existing
    // business mid-wizard must drop everything tied to the return that was
    // loaded for the PREVIOUS business (returnId included), or the wizard
    // keeps saving/paying against that old business's TaxReturn row even
    // though state.biz/businessId (and everything the UI displays, e.g.
    // Review) already point at the new one. Wizard.jsx's fetch-or-create
    // effect (keyed on state.businessId) then reloads/creates the NEW
    // business's own draft once returnType is known, same as a fresh
    // filing - see that effect's guard on state.returnTotals/state.returnId.
    //
    // forceFreshDraft:true steers that effect toward a genuinely NEW draft
    // rather than silently resuming whatever half/new/full draft already
    // happens to exist for the newly-picked business - reused, that draft can
    // be hours/days old (its own trucks, month, everything) and the filer has
    // no idea they just inherited it instead of starting clean. This is
    // specifically for hand-picking a business mid-wizard (New Filing); the
    // dashboard's explicit "Continue filing" buttons never go through here
    // (they set businessId before this step even mounts), so they keep
    // resuming a business's existing draft exactly as intended.
    case "RESET_RETURN":
      return {
        ...state,
        returnId: null,
        returnTotals: null,
        perCategory: [],
        trucks: [],
        mileageVehicles: [],
        amendVehicles: [],
        correctedVins: [],
        rejectedResubmission: false,
        originalVehicleCount: null,
        remainingVehicleCapacity: null,
        extraVehiclesPendingFee: null,
        forceFreshDraft: true,
        // A different business's draft is about to load - its own preparer override (or
        // lack of one) must replace whatever was picked for the PREVIOUS business's return.
        returnTaxPreparerId: null,
      };
    case "SET_TOTALS":
      return { ...state, returnTotals: { ...state.returnTotals, ...action.patch } };
    case "SET_LOOKUPS":
      return {
        ...state,
        weightCategoriesLookup: action.weightCategories ?? state.weightCategoriesLookup,
        monthsLookup: action.months ?? state.monthsLookup,
        taxYearsLookup: action.taxYears ?? state.taxYearsLookup,
      };
    case "SET_PAY":
      return { ...state, pay: { ...state.pay, ...action.patch } };
    case "SET_SERVICE_FEE":
      return { ...state, serviceFee: { ...state.serviceFee, ...action.patch } };
    case "FILE":
      return { ...state, filed: true, filedResult: action.result || null };
    case "SET_TOAST":
      return { ...state, toast: action.msg };
    default:
      return state;
  }
}

export const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const toastTimer = useRef(null);

  // Keeps sessionStorage in sync with these three ids as they change, so a
  // hard refresh on any wizard step restores them (see PERSISTED_ID_KEYS
  // above and initialState's loadPersistedId calls).
  useEffect(() => {
    persistId(PERSISTED_ID_KEYS.businessId, state.businessId);
  }, [state.businessId]);
  useEffect(() => {
    persistId(PERSISTED_ID_KEYS.preparerId, state.preparerId);
  }, [state.preparerId]);
  useEffect(() => {
    persistId(PERSISTED_ID_KEYS.returnId, state.returnId);
  }, [state.returnId]);
  useEffect(() => {
    persistString(PERSISTED_STRING_KEYS.returnType, state.returnType);
  }, [state.returnType]);
  useEffect(() => {
    persistString(PERSISTED_STRING_KEYS.amendReason, state.amendReason);
  }, [state.amendReason]);

  const showToast = useCallback((msg) => {
    dispatch({ type: "SET_TOAST", msg });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => dispatch({ type: "SET_TOAST", msg: "" }), 2600);
  }, []);

  return (
    <AppContext.Provider value={{ state, dispatch, showToast }}>
      {children}
    </AppContext.Provider>
  );
}
